/* Step 7a (owner card "AZEX Step 7 Card V2", 29-Oct-2026) — the evaluation of one sample, opened from its row on the
   Sampling page (Evaluate). Reads GET /samples/{id}/evaluation (routes_sample_eval.py) and shows: the selection's label
   (approach A risk-based "not sampling", S14 "100 % examination – not a sample", S16 specific vouching), the working-paper
   lines (S1 interval and start, S2 population order, S4 working hours, S10 credit / zero lines), the items (S18 / S19 / S20),
   Evaluate / Re-evaluate (an earlier-method row keeps its saved figures — "Re-evaluate" writes a new row), the result, the
   conclusion (S21) and the sampling-sheet sign-off bar (Preparer + Reviewer 1). New code in its own file: Sampling.jsx keeps
   one button and one render line. */
import React, { useCallback, useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SignOffBar from "@/components/SignOffBar";
import EvaluationItems from "@/pages/Sampling/EvaluationItems";
import EvaluationResults from "@/pages/Sampling/EvaluationResults";
import { methodText, workingPaperLines, fill, cnt, amt, plainStandard } from "@/pages/Sampling/evaluationFormat";

export default function EvaluationPanel({ sampleId, engId, locked }) {
  const { t, lang } = useI18n();
  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/samples/${sampleId}/evaluation`);
      setState(r.data); setErr("");
    } catch (e) { setErr(toErrMsg(e, t("s7a.panel.loadFailed"))); }
  }, [sampleId, t]);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); setRk((n) => n + 1); };

  const evaluate = async () => {
    setBusy(true);
    try {
      await api.post(`/samples/${sampleId}/evaluation`);
      toast.success(t("s7a.panel.evaluatedToast"));
      reload();
    } catch (e) { toast.error(toErrMsg(e, t("s7a.panel.saveFailed"))); } finally { setBusy(false); }
  };

  if (err) return <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900" data-testid="ev-error">{err}</div>;
  if (!state) return <div className="text-xs text-muted-foreground">{t("common.loading")}</div>;
  const c = state.counts || {};
  const lines = workingPaperLines(state, t, lang);
  const notASample = state.method_kind === "not_a_sample" || state.method_kind === "hundred_pct";
  return (
    <div className="space-y-3 rounded border border-sky-200 bg-sky-50/30 p-3 text-xs dark:bg-sky-950/10" data-testid={`ev-panel-${sampleId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{t("s7a.panel.title")} — {state.name}</div>
          <div data-testid="ev-method">{t("sampling.method")}: {methodText(state.method, t)}
            {state.standard ? <span className="ms-1 text-muted-foreground" dir="ltr">({plainStandard(state.standard)})</span> : null}</div>
          {state.confidence_level != null && (
            <div>{t("sampling.confPrefix")} {Math.round(Number(state.confidence_level) * 1000) / 10}%
              {state.sampling_interval ? ` · ${t("sampling.intervalPrefix")} ${amt(state.sampling_interval)}` : ""}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!locked && (
            <Button size="sm" onClick={evaluate} disabled={busy || c.pending > 0} data-testid="ev-evaluate"
                    title={c.pending > 0 ? fill(t("s7a.panel.pendingCount"), { n: cnt(c.pending) }) : undefined}>
              {state.latest_evaluation ? t("s7a.panel.reEvaluateBtn") : t("s7a.panel.evaluateBtn")}
            </Button>
          )}
        </div>
      </div>
      <ul className="list-disc space-y-0.5 ps-5" data-testid="ev-working-paper">
        {lines.map((l) => <li key={l.key} data-testid={`ev-wp-${l.key}`}>{l.text}</li>)}
      </ul>
      {state.earlier_method && (
        <div className="rounded border border-amber-300 bg-amber-50 p-1.5 text-amber-900" data-testid="ev-earlier-banner">{t("s7a.earlierMethod")}</div>
      )}
      {state.stale && (
        <div className="rounded border border-amber-300 bg-amber-50 p-1.5 text-amber-900" data-testid="ev-stale">{t("s7a.panel.staleBanner")}</div>
      )}
      <div data-testid="ev-counts-line">{fill(t("s7a.panel.itemsLine"), { items: cnt(c.items), e: cnt(c.evaluated), p: cnt(c.pending),
                                                                         na: cnt(c.not_applicable), u: cnt(c.untestable?.count || 0),
                                                                         r: cnt(c.replaced?.count || 0), a: cnt(c.anomalies?.count || 0) })}</div>
      <EvaluationItems sampleId={sampleId} items={state.items || []} t={t} locked={locked} onSaved={reload} notASample={notASample} />
      <div className="border-t pt-2">
        <div className="mb-1 text-sm font-semibold">{t("s7a.panel.resultsTitle")}</div>
        <EvaluationResults state={state} t={t} locked={locked} onChanged={reload} />
      </div>
      <SignOffBar engId={engId} itemType="sampling" itemKey={sampleId} label={t("itemSignoff.samplingSheet")} refreshKey={rk}
                  onChange={load} />
    </div>
  );
}
